import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import MetricsCard from './components/MetricsCard';
import PerformanceChart from './components/PerformanceChart';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import NotificationPanel from './components/NotificationPanel';
import MessagingCenter from './components/MessagingCenter';
import KnowledgeBase from './components/KnowledgeBase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PartnerDashboard = () => {
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [partnerData, setPartnerData] = useState({
    name: 'Alexandre Dubois',
    role: 'Agent Musical',
    joinDate: '2023-03-15',
    totalCommissions: 15750.25,
    activeCampaigns: 8,
    conversionRate: 12.5,
    pendingPayments: 2340.80
  });

  const [dashboardMetrics, setDashboardMetrics] = useState([
    {
      title: 'Commissions totales',
      value: 15750.25,
      change: 8.2,
      changeType: 'positive',
      icon: 'DollarSign',
      currency: true
    },
    {
      title: 'Campagnes actives',
      value: 8,
      change: 2.1,
      changeType: 'positive',
      icon: 'Target',
      currency: false
    },
    {
      title: 'Taux de conversion',
      value: 12.5,
      change: -1.3,
      changeType: 'negative',
      icon: 'TrendingUp',
      currency: false
    },
    {
      title: 'Paiements en attente',
      value: 2340.80,
      change: 15.7,
      changeType: 'positive',
      icon: 'Clock',
      currency: true
    }
  ]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for saved language preference
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Simulate data loading
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Data is already set in state, this would normally come from API
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(new Date(dateString));
  };

  const getCurrentTime = () => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement du tableau de bord...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Bonjour, {partnerData?.name} 👋
                </h1>
                <p className="text-muted-foreground">
                  {getCurrentTime()} • Partenaire depuis {formatDate(partnerData?.joinDate)}
                </p>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => console.log('Export dashboard data')}
                >
                  Exporter
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => console.log('Create new campaign')}
                >
                  Nouvelle campagne
                </Button>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {dashboardMetrics?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                currency={metric?.currency}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Performance Chart - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <PerformanceChart />
            </div>
            
            {/* Quick Actions */}
            <div>
              <QuickActions />
            </div>
          </div>

          {/* Secondary Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-8">
            {/* Activity Feed */}
            <div>
              <ActivityFeed />
            </div>
            
            {/* Messaging Center */}
            <div>
              <MessagingCenter />
            </div>
            
            {/* Notifications Panel */}
            <div className="lg:col-span-2 xl:col-span-1">
              <NotificationPanel />
            </div>
          </div>

          {/* Knowledge Base Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <KnowledgeBase />
            </div>
            
            {/* Additional Stats or Info Panel */}
            <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">
                Statistiques rapides
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Users" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-card-foreground">
                      Artistes représentés
                    </span>
                  </div>
                  <span className="text-lg font-bold text-card-foreground">24</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={20} className="text-success" />
                    <span className="text-sm font-medium text-card-foreground">
                      Venues partenaires
                    </span>
                  </div>
                  <span className="text-lg font-bold text-card-foreground">156</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Calendar" size={20} className="text-warning" />
                    <span className="text-sm font-medium text-card-foreground">
                      Événements ce mois
                    </span>
                  </div>
                  <span className="text-lg font-bold text-card-foreground">18</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Star" size={20} className="text-error" />
                    <span className="text-sm font-medium text-card-foreground">
                      Note moyenne
                    </span>
                  </div>
                  <span className="text-lg font-bold text-card-foreground">4.8/5</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="BarChart3"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => console.log('View detailed analytics')}
                >
                  Voir les analyses détaillées
                </Button>
              </div>
            </div>
          </div>

          {/* Footer Section */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} Artis.io. Tous droits réservés.
              </p>
              <div className="mt-4 sm:mt-0 flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="HelpCircle"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Open help')}
                >
                  Aide
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Contact support')}
                >
                  Support
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Settings"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Open settings')}
                >
                  Paramètres
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerDashboard;